import { useEffect, useState, type CSSProperties } from "react";
import {
  HERO_DECK_ITEMS,
  heroDeckQueueGray,
  heroDeckStackOffset,
  heroDeckStackJitterPx,
  heroDeckStackTwistDeg,
  type HeroDeckItem,
} from "./heroDeckItems.js";

const ROTATE_MS = 3000;

function HeroWindow({ name, shotSrc, shotAlt }: { name: string; shotSrc: string; shotAlt: string }) {
  return (
    <div className="lm-window lm-window-hero">
      <div className="lm-window-chrome">
        <span className="lm-window-dot lm-window-dot-close" />
        <span className="lm-window-dot lm-window-dot-min" />
        <span className="lm-window-dot lm-window-dot-zoom" />
      </div>
      <div className="lm-window-screen lm-hero-deck-screen">
        <img
          className="lm-window-shot"
          src={shotSrc}
          alt={shotAlt}
          width={1200}
          height={630}
          loading="eager"
          decoding="async"
        />
        <span className="lm-hero-deck-label">{name}</span>
      </div>
    </div>
  );
}

/** Client captures + app windows in a rotating 3D deck (fan + back stack). */
export function HeroSiteDeck({ items }: { items?: readonly HeroDeckItem[] }) {
  const deck = items && items.length > 0 ? items : HERO_DECK_ITEMS;
  const count = deck.length;
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const id = window.setInterval(() => {
      setPhase((p) => (p + 1) % count);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [count]);

  return (
    <div className="lm-hero-deck" aria-hidden="true">
      {deck.map((site, cardIndex) => {
        const slot = heroDeckStackOffset(cardIndex, phase, count);
        const jitter = heroDeckStackJitterPx(cardIndex);
        return (
          <div
            key={`${site.name}-${cardIndex}`}
            className="lm-hero-deck-card"
            data-slot={slot}
            data-site={site.name}
            style={
              {
                "--lm-deck-twist": `${heroDeckStackTwistDeg(cardIndex)}deg`,
                "--lm-deck-jitter-x": `${jitter.x}px`,
                "--lm-deck-jitter-y": `${jitter.y}px`,
                "--lm-deck-queue-gray": heroDeckQueueGray(cardIndex, count),
              } as CSSProperties
            }
          >
            <HeroWindow name={site.name} shotSrc={site.shotSrc} shotAlt={site.shotAlt} />
          </div>
        );
      })}
    </div>
  );
}
